import type { SvgIconComponent } from '@mui/icons-material'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import ScreenShareOutlinedIcon from '@mui/icons-material/ScreenShareOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'

/**
 * The orange panel beside the sign-in, sign-up, email code, invite, join-link and password screens
 * (SC-438).
 * It sells the product at the moment someone is deciding to come in. Nothing here may promise a
 * feature that hasn't shipped (recording and AI notes are not live yet).
 */
export type AuthBrandScreen = 'signIn' | 'signUp' | 'verify' | 'invite' | 'joinLink'

export interface AuthBrandCopy {
  title: string
  subtitle: string
  points: ReadonlyArray<{ icon: SvgIconComponent; text: string }>
}

export const AUTH_BRAND_COPY: Record<AuthBrandScreen, AuthBrandCopy> = {
  signIn: {
    title: 'Welcome back to the office',
    subtitle: 'Your team’s probably already around.',
    points: [
      { icon: VisibilityOutlinedIcon, text: 'See who’s in and free right now' },
      { icon: ChatBubbleOutlineIcon, text: 'Talk or chat instantly — no calls to schedule' },
      { icon: PersonAddAltOutlinedIcon, text: 'Invite teammates and guests in one click' },
      { icon: EventAvailableOutlinedIcon, text: 'Your rooms and booked meetings, ready to join' },
    ],
  },
  signUp: {
    title: 'Your own office in a minute',
    subtitle: 'Not another meeting link — a place your team walks into.',
    points: [
      { icon: MapOutlinedIcon, text: 'Pick a layout that fits how you work' },
      { icon: ScreenShareOutlinedIcon, text: 'Talk, chat and share your screen in the same room' },
      { icon: GroupsOutlinedIcon, text: 'Free for 30 days — your whole team' },
      { icon: MeetingRoomOutlinedIcon, text: 'Rooms for standups, pairing and the lounge' },
    ],
  },
  verify: {
    title: 'Almost in',
    subtitle: 'One more step — confirm it’s you, then your office is ready.',
    points: [
      { icon: ApartmentOutlinedIcon, text: 'Your whole team on one floor, online or away at a glance' },
      { icon: DirectionsWalkIcon, text: 'Walk up to anyone to talk, no links or scheduling' },
      { icon: ScreenShareOutlinedIcon, text: 'Share your screen in any room' },
      { icon: EventAvailableOutlinedIcon, text: 'Book meeting rooms and bring in outside guests' },
    ],
  },
  invite: {
    title: 'Welcome to the office',
    subtitle: 'Set a password and walk straight in — your team’s already inside.',
    points: [
      { icon: VisibilityOutlinedIcon, text: 'See who’s in and free right now' },
      { icon: ChatBubbleOutlineIcon, text: 'Talk or chat the moment you’re near' },
      { icon: MeetingRoomOutlinedIcon, text: 'Standups, pairing and the lounge — just walk in' },
      { icon: EventAvailableOutlinedIcon, text: 'Your rooms and booked meetings, ready to join' },
    ],
  },
  /** The workspace invite link page: no password to set, the person just joins. */
  joinLink: {
    title: 'Your team’s already inside',
    subtitle: 'Join them and you’re on the same floor in a moment.',
    points: [
      { icon: VisibilityOutlinedIcon, text: 'See who’s in and free right now' },
      { icon: DirectionsWalkIcon, text: 'Walk up to anyone to talk, no links or scheduling' },
      { icon: ScreenShareOutlinedIcon, text: 'Share your screen in any room' },
      { icon: EventAvailableOutlinedIcon, text: 'Your rooms and booked meetings, ready to join' },
    ],
  },
}
